import { NamedPromise, executeCriticalPromises, executeDegradablePromises } from './interaction-tracker/promiseExecutor'

import type { RouteLocationNormalized } from 'vue-router'
import { User } from '../models'
import cloneDeep from 'lodash/cloneDeep'
import { conversationRouteNames } from '../routes/constants/location'
import store from '../store'
import { universityRoutes } from '../routes/constants/agency/'

export const dispatchCustomEvents = (event: string, params: { [key: string]: any }) => {
  if (CustomEvent !== undefined) {
    window.dispatchEvent(new CustomEvent(event, params))
  }
}

export const refreshFirebaseToken = async (loc_id: string) => {
  await store.dispatch('auth/refreshFirebaseToken', { locationId: loc_id || null, refresh: true }, { root: true })
}

export const handleExternalRedirect = async (to: RouteLocationNormalized) => {
  let url: string = to.meta.RedirectExternalUrl as string
  if (to.name == universityRoutes.default) {
    try {
      const currentUser = await store.dispatch('user/get')
      const user = new User(cloneDeep(currentUser))
      url += `&email=${user.email}`
    } catch {}
  }
  window.open(url, '_blank')?.focus()
}

export const dispatchConversationsV2SyncAll = async (to: RouteLocationNormalized, from: RouteLocationNormalized) => {
  if (from.name === conversationRouteNames.conversationsIdV2 && from.name !== to.name) {
    // mytodo: check if any change is needed in syncAllV2  & is dispatchConversationsV2SyncAll relevent for group chat
    await store.dispatch('conversationV2/syncAllV2', {
      locationId: to.params.location_id,
      noListener: true,
      stickyFilterEnabled: store?.getters?.['featureFlags/getForLocation']('conversations.stickyFilters'),
    })
  }
}

/**
 * Dispatches all sync operations required during location switch.
 *
 * Operations are categorized by criticality:
 * - BLOCKING: User waits for these - app won't function without them
 * - NON-BLOCKING: Awaited with timeout - failures are logged but don't break the app
 *
 * @param loc_id - The location ID to sync data for
 * @param userId - The current user ID
 * @param routePathParams - Optional route parameters
 */
export const dispatchSyncAlls = async (loc_id: string, userId: string, routePathParams?: any) => {
  // Critical operations - must succeed
  const criticalBlockingOperations = executeCriticalPromises(
    [
      {
        name: 'locations/resetCurrentLocation',
        promise: store.dispatch('locations/resetCurrentLocation', { locationId: loc_id }),
      },
      { name: 'users/syncAll', promise: store.dispatch('users/syncAll', loc_id) },
      // Keep feature flags blocking so UI/routes can evaluate flags immediately after location switch.
      { name: 'featureFlags/syncAll', promise: store.dispatch('featureFlags/syncAll', loc_id) },
    ],
    { context: 'RouterHelper.dispatchSyncAlls' }
  )

  // Degradable operations - 90s timeout, can fail gracefully
  const degradableNonBlockingOperations: NamedPromise[] = [
    { name: 'customObjectsStore/syncAll', promise: store.dispatch('customObjectsStore/syncAll', loc_id) },
    {
      name: 'locationCustomFields/syncAll',
      promise: store.dispatch('locationCustomFields/syncAll', { locationId: loc_id }),
    },
    { name: 'conversationV2/updatePhoneNumbers', promise: store.dispatch('conversationV2/updatePhoneNumbers', []) },
    {
      name: 'conversationAi/fetchConversationAIOptInForLocation',
      promise: store.dispatch('conversationAi/fetchConversationAIOptInForLocation', { entityId: loc_id }),
    },
    { name: 'wordpress/syncAll', promise: store.dispatch('wordpress/syncAll', loc_id) },
    { name: 'wordpress/fetchOfferingEnabled', promise: store.dispatch('wordpress/fetchOfferingEnabled', loc_id) },
    // aiLocationPlan/fetchShowSummerOfAIMenu removed - Summer of AI feature is disabled (getter always returns false)
    {
      name: 'calendars/syncSubAccountConfig',
      promise: new Promise(async (resolve, reject) => {
        store.commit('calendars/set', { subAccountConfig: undefined })
        store.dispatch('calendars/syncSubAccountConfig', loc_id).then(resolve).catch(reject)
      }),
    },
    {
      name: 'conversationChannels/updateDefaultChannels',
      promise: store.dispatch('conversationChannels/updateDefaultChannels', {}),
    },
  ]

  // Fire-and-forget degradable operations (do not block navigation/location switch).
  executeDegradablePromises(degradableNonBlockingOperations, {
    context: 'RouterHelper.dispatchSyncAlls.degradable',
    onError: (name, error) => console.error(`[RouterHelper.dispatchSyncAlls] ${name} failed:`, error),
  })

  // Only wait for critical operations.
  await criticalBlockingOperations
}

export const getHTMLElements = () => {
  const element = document.getElementById('app')
  const htmlTag = document.getElementsByTagName('html')[0]
  htmlTag.className = 'overflow-hidden lead-connector'
  return [element, htmlTag]
}

export const setSidebarState = (to: RouteLocationNormalized, from: RouteLocationNormalized) => {
  const sideBarRoute = ['contact_detail']
  if (typeof to.name === 'string' && sideBarRoute.includes(to.name)) {
    store.commit('setSideBarState', true)
  } else if (typeof from.name === 'string' && sideBarRoute.includes(from.name)) {
    store.commit('setSideBarState', false)
  }

  if (
    (typeof to.name === 'string' && sideBarRoute.includes(to.name)) ||
    (typeof from.name === 'string' && sideBarRoute.includes(from.name))
  ) {
    store.commit('setSideBarState', false)
  }
}
